import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPixelsSummary, PixelsSummary } from '../../services/pixelsService';
import { resolveBillingStatus } from './billingStatus';
import PixlesCoin from './PixlesCoin';
import '../../pages/billing/billing.css';

/**
 * Home overview billing status card: pixels balance + exactly one plan
 * status line, with CTAs that route to /billing (checkout lives ONLY there).
 * Status display + routing — never a second checkout.
 *
 * Fails silent: billing fetch errors render nothing so the home page never
 * breaks on a billing outage.
 */
const BillingStatusCard: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<PixelsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setLoading(false);
      setFailed(true);
      return;
    }
    let cancelled = false;
    void getPixelsSummary(token)
      .then((loaded) => {
        if (cancelled) return;
        setSummary(loaded);
      })
      .catch(() => {
        if (cancelled) return;
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <section
        className="billing-card billing-status-card is-loading"
        aria-busy="true"
        aria-label="Loading billing status"
      >
        <div className="billing-skeleton-line billing-skeleton-line-short" />
        <div className="billing-skeleton-line" />
      </section>
    );
  }

  if (failed) return null;

  const model = resolveBillingStatus(summary);

  return (
    <section
      className="billing-card billing-status-card billing-status-card--split"
      aria-label="Billing status"
    >
      <div className="billing-status-card-main">
        <p className="billing-status-card-balance">
          <PixlesCoin size={28} />
          {model.line1}
        </p>
        <p className="billing-status">{model.line2}</p>
      </div>
      <div className="billing-status-card-side billing-status-card-pair">
        <div
          className="billing-status-card-pair-track"
          role="group"
          aria-label="Billing actions"
        >
          {model.ctas.map((cta) => {
            const isPrimary = cta.variant === 'primary';
            return (
              <button
                key={cta.label}
                type="button"
                className={
                  'billing-status-card-pair-seg' +
                  (isPrimary ? ' is-primary' : '')
                }
                onClick={() => navigate(cta.to)}
              >
                {isPrimary && (
                  <span className="billing-status-card-pair-coin">
                    <PixlesCoin size={14} />
                  </span>
                )}
                <span>{cta.label}</span>
              </button>
            );
          })}
        </div>
        {model.note && <p className="billing-status-card-note">{model.note}</p>}
      </div>
    </section>
  );
};

export default BillingStatusCard;
